import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { HTTPRequestMethod } from '@app/core/enums/http-request-method.enum';
import { MimeType } from '@app/core/enums/mime-type.enum';
import { LocalStorageService } from '../storage/local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class DataService {
    constructor(
        private apiService: ApiService,
        private localService: LocalStorageService
    ) { }

    getCastMembers(query?: string): Observable<any> {
        let url = 'api/cast/search';
        if (query) {
            url = `${url}?query=${query}`;
        }

        return this.apiService.sendRequest(
            url,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getCastMemberByPernr(pernr: string): Observable<any> {
        return this.apiService.sendRequest(
            `api/cast/${pernr}`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getCharacters(query?: string): Observable<any> {
        let url = 'api/character/search';
        if (query) {
            url = `${url}?query=${query}`;
        }

        return this.apiService.sendRequest(
            url,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getCharactersLoadSelectByRegionId(regionId: string): Observable<any> {
        const url = `api/character/load/${regionId}`;

        return this.apiService.sendRequest(
            url,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getCharactersByRegionId(regionId: string): Observable<any> {
        const url = `api/character/search?filter=${encodeURI(JSON.stringify({ region_id: regionId }))}`;

        return this.apiService.sendRequest(
            url,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getCharacterByCharacterId(characterId: string): Observable<any> {
        return this.apiService.sendRequest(
            `api/character/${characterId}`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getSiteLocations(): Observable<any> {
        return this.apiService.sendRequest(
            `api/sites`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getRegions(): Observable<any> {
        return this.apiService.sendRequest(
            `api/regions`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getMeasurementsByDate(pernr: string, date: string): Observable<any> {
        return this.apiService.sendRequest(
            `api/measurements/${pernr}?date=${date}`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getEligibleCasts(characterId: string, matchId: string): Observable<any> {
        return this.apiService.sendRequest(
            `api/character/eligibility/${characterId}?range_id=${matchId}`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    uploadCastMeasurements(measurements: any[]): Observable<any> {
        measurements[0].scan_comment = 'Updated by Cast Member Measurements Upload';

        return this.apiService.sendRequest(
            `api/cast/measurements/mass-update`,
            HTTPRequestMethod.Post,
            MimeType.JSON,
            measurements
        );
    }

    getCastEligibility(regionId: string, measurements: string, characterId = ''): Observable<any> {
        let url = `api/cast/eligibility?region_id=${regionId}&measurements=${measurements}`;

        if (characterId) {
            url += `&character_id=${characterId}`;
        }

        return this.apiService.sendRequest(
            url,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    checkCastProfile(pernr: string): Observable<any> {
        return this.apiService.sendRequest(
            `api/cast/measurements/check?pernr=${pernr}`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    updateCharacterMeasurements(characterId: any, measurements: any): Observable<any> {
        const reqBody = {
            'character_id': characterId,
            'measurements': measurements
        };

        return this.apiService.sendRequest(
            `api/character/update`,
            HTTPRequestMethod.Post,
            MimeType.JSON,
            reqBody
        );
    }

    createCastMeasurements(params: any): Observable<any> {
        return this.apiService.sendRequest(
            `/api/cast/measurements/create`,
            HTTPRequestMethod.Post,
            MimeType.JSON,
            params
        );
    }

    /**
     * Update cast member measurements
     * @param {String} site code
     * @param {String} personnel number
     * @param {String} cast role
     * @param {Object} cast measurements
     * @returns {Object} observable
     */
    updateCastMeasurements(hubId: string, siteCode: string,
        pernr: string, role: string, scanComment: string,
        measurements: any): Observable<any> {
        const body = {
            'personnel_number': pernr,
            'site': siteCode,
            'role': role || '',
            'scan_comment': scanComment || '',
            'measurements': measurements,
            hub_id: hubId
        };

        return this.apiService.sendRequest(
            `/api/cast/measurements/update`,
            HTTPRequestMethod.Post,
            MimeType.JSON,
            body
        );
    }

    createCharacter(character: any): Observable<any> {
        const body = character;

        // Reset characters from storage
        this.localService.removeData('region1Characters');
        this.localService.removeData('region2Characters');

        return this.apiService.sendRequest(
            `/api/character/save`,
            HTTPRequestMethod.Post,
            MimeType.JSON,
            body
        );
    }

    checkCharacter(characterName: any): Observable<any> {
        return this.apiService.sendRequest(
            `/api/character/check?character_name=${characterName}`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    checkImage(imageId: any): Observable<any> {
        return this.apiService.sendRequest(
            `/api/character/image?iid=${imageId}`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getAllMeasurements(): Observable<any> {
        return this.apiService.sendRequest(
            `api/cast/measurements/get-all-services`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getMeasurementOffset(path: any): Observable<any> {
        return this.apiService.sendRequest(
            `${path}`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getAllCharacterMeasurements(): Observable<any> {
        return this.apiService.sendRequest(
            `api/character/meas/get-all-serv`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    updateCharacterProfile(character: any): Observable<any> {
        const body = character;

        // Reset characters from storage
        this.localService.removeData('region1Characters');
        this.localService.removeData('region2Characters');

        return this.apiService.sendRequest(
            `/api/character/update?profileUpdate=true`,
            HTTPRequestMethod.Post,
            MimeType.JSON,
            body
        );
    }

    getStatus(): Observable<any> {
        return this.apiService.sendRequest(
            `/api/character/status`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    getRoles(): Observable<any> {
        return this.apiService.sendRequest(
            `api/roles`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    updateCastProfile(cast: any): Observable<any> {
        const body = cast;

        return this.apiService.sendRequest(
            `/api/cast/update`,
            HTTPRequestMethod.Post,
            MimeType.JSON,
            body
        );
    }

    checkUser(hubId: any): Observable<any> {
        return this.apiService.sendRequest(
            `api/user-profile/${hubId}`,
            HTTPRequestMethod.Get,
            MimeType.JSON,
            null
        );
    }

    createUnitMeasure(cast: any): Observable<any> {
        const body = cast;

        return this.apiService.sendRequest(
            `/api/user-profile/attribute`,
            HTTPRequestMethod.Post,
            MimeType.JSON,
            body
        );
    }
}
